/*
 * SundayBuilder × Choral Intelligence™ · Right-Hand Rail
 * ---------------------------------------------------------------------
 * Deterministic recommendation strip that sits alongside the planner
 * on Screen 2 (Context Notes). Shows the top choral candidates for
 * the resolved Sunday (cross-moment discernment view) — same pipeline
 * as the inline "Suggest anthem" trigger, no second algorithm.
 *
 * Mobile behavior: collapses into a bottom-sheet drawer triggered by
 * a compact summary chip.
 * =================================================================== */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Plus, Check } from "lucide-react";
import RecommendationExplanation from "./RecommendationExplanation";
import HymnalAvailability from "../repertoire/HymnalAvailability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pct(x) {
  if (typeof x !== "number" || Number.isNaN(x)) return "—";
  return `${Math.round(x * 100)}%`;
}

function statusLabel(s) {
  const m = {
    verified: "Verified",
    editor_reviewed: "Editorial Reviewed",
    ai_reviewed: "AI Reviewed",
    draft: "Draft",
  };
  return m[s] || s;
}

function RailItem({ rec, open, onToggle, sundayDate, onPicked }) {
  const { work, ranking, explanation } = rec;
  const [pickState, setPickState] = useState("idle"); // idle | saving | picked | error
  const [pickError, setPickError] = useState(null);

  const handlePick = async (e) => {
    e.stopPropagation();
    if (!sundayDate || pickState === "saving" || pickState === "picked") return;
    setPickState("saving");
    setPickError(null);
    try {
      await axios.post(`${API}/sundaybuilder/choral-picks`, {
        sunday_date:       sundayDate,
        liturgical_moment: "offertory",
        work_id:           work.id,
        work_title:        work.title,
        work_composer:     work.composer,
      });
      setPickState("picked");
      if (onPicked) onPicked(work.id);
    } catch (err) {
      setPickState("error");
      setPickError(err.response?.data?.detail || "Could not add to plan.");
    }
  };

  return (
    <li
      className="sb-choral-rail__item"
      data-testid={`sb-choral-rail-item-${work.id}`}
    >
      <button
        type="button"
        className="sb-choral-rail__item-head"
        aria-expanded={open}
        aria-controls={`sb-choral-rail-body-${work.id}`}
        onClick={onToggle}
        data-testid={`sb-choral-rail-toggle-${work.id}`}
      >
        <div className="sb-choral-rail__item-title-block">
          <div
            className="sb-choral-rail__item-title"
            data-testid={`sb-choral-rail-title-${work.id}`}
          >
            {work.title}
          </div>
          <div
            className="sb-choral-rail__item-composer"
            data-testid={`sb-choral-rail-composer-${work.id}`}
          >
            {work.composer || "Anonymous"}
          </div>
        </div>
        <div className="sb-choral-rail__item-fit">
          <span
            className="sb-choral-rail__fit"
            data-testid={`sb-choral-rail-fit-${work.id}`}
          >
            {pct(ranking?.total_score)}
          </span>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>
      {open && (
        <div
          id={`sb-choral-rail-body-${work.id}`}
          className="sb-choral-rail__item-body"
          data-testid={`sb-choral-rail-body-${work.id}`}
        >
          <p className="sb-choral-rail__status">
            {statusLabel(work.verification_status)}
            {" · "}
            <span className="sb-choral-rail__catalog-id">{work.id}</span>
          </p>
          <RecommendationExplanation explanation={explanation} />
          <HymnalAvailability availability={rec.availability || []} />
          {sundayDate && (
            <div className="sb-choral-rail__pick-row">
              <button
                type="button"
                className={`sb-choral-rail__pick-btn sb-choral-rail__pick-btn--${pickState}`}
                onClick={handlePick}
                disabled={pickState === "saving" || pickState === "picked"}
                data-testid={`sb-choral-rail-pick-${work.id}`}
              >
                {pickState === "picked" ? (
                  <><Check size={13} /> Added to plan</>
                ) : pickState === "saving" ? (
                  "Adding…"
                ) : (
                  <><Plus size={13} /> Add to plan</>
                )}
              </button>
              {pickError && (
                <span className="sb-choral-rail__pick-err" data-testid={`sb-choral-rail-pick-err-${work.id}`}>
                  {pickError}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function SundayBuilderChoralRail({
  date,
  limit = 5,
  variant = "rail",   // "rail" (desktop) | "drawer" (mobile trigger)
}) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [openId, setOpenId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState(new Set());

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    axios
      .post(`${API}/sundaybuilder/choral-recommendations`, {
        date,
        limit,
        // Rail is the discernment/cross-moment view — no moment filter.
      })
      .then((r) => {
        if (cancelled) return;
        setState({ loading: false, error: null, data: r.data });
        // Auto-open the top result so the director sees the "Because…"
        // panel without clicking.
        const top = r.data?.recommendations?.[0]?.work?.id;
        if (top) setOpenId(top);
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: e.response?.data?.detail || "Choral recommendations are temporarily unavailable.",
          data: null,
        });
      });

    // Fetch existing picks so the button reads "Added to plan" for
    // works the director has already picked (planning ≠ performance —
    // this reflects state, doesn't touch Parish Memory).
    axios
      .get(`${API}/sundaybuilder/choral-picks`, { params: { sunday_date: date } })
      .then((r) => {
        if (cancelled) return;
        const ids = new Set((r.data?.picks || []).map((p) => p.work_id));
        setPickedIds(ids);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [date, limit]);

  const toggle = useCallback((id) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  const registerPicked = useCallback((wid) => {
    setPickedIds((prev) => {
      const next = new Set(prev);
      next.add(wid);
      return next;
    });
  }, []);

  const total = state.data?.total ?? 0;
  const recs = state.data?.recommendations ?? [];
  const context = state.data?.context;

  // Mobile: compact chip trigger + off-canvas drawer.
  if (variant === "drawer") {
    return (
      <>
        <button
          type="button"
          className="sb-choral-rail__chip"
          onClick={() => setDrawerOpen(true)}
          data-testid="sb-choral-rail-chip"
        >
          Choral Intelligence™
          {total > 0 && (
            <span className="sb-choral-rail__chip-count" data-testid="sb-choral-rail-chip-count">
              {total} candidates
            </span>
          )}
        </button>
        {drawerOpen && (
          <div
            className="sb-choral-rail__drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
            data-testid="sb-choral-rail-drawer-backdrop"
          >
            <aside
              className="sb-choral-rail__drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Choral Intelligence recommendations"
              onClick={(e) => e.stopPropagation()}
              data-testid="sb-choral-rail-drawer"
            >
              <div className="sb-choral-rail__drawer-head">
                <h3 className="sb-choral-rail__title">Choral Intelligence™</h3>
                <button
                  type="button"
                  className="sb-choral-rail__drawer-close"
                  onClick={() => setDrawerOpen(false)}
                  data-testid="sb-choral-rail-drawer-close"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <RailBody state={state} recs={recs} context={context} openId={openId} toggle={toggle} sundayDate={date} pickedIds={pickedIds} onPicked={registerPicked} />
            </aside>
          </div>
        )}
      </>
    );
  }

  // Desktop rail.
  return (
    <aside
      className="sb-choral-rail"
      aria-label="Choral Intelligence recommendations"
      data-testid="sb-choral-rail"
    >
      <div className="sb-choral-rail__head">
        <h3 className="sb-choral-rail__title">Choral Intelligence™</h3>
        {context?.occasion && (
          <p className="sb-choral-rail__subtitle" data-testid="sb-choral-rail-occasion">
            for {context.occasion}
          </p>
        )}
      </div>
      <RailBody state={state} recs={recs} context={context} openId={openId} toggle={toggle} sundayDate={date} pickedIds={pickedIds} onPicked={registerPicked} />
    </aside>
  );
}

function RailBody({ state, recs, context, openId, toggle, sundayDate, pickedIds, onPicked }) {
  if (state.loading) {
    return (
      <p className="sb-choral-rail__note" data-testid="sb-choral-rail-loading">
        Consulting the governed catalog…
      </p>
    );
  }
  if (state.error) {
    return (
      <p className="sb-choral-rail__note sb-choral-rail__note--error" data-testid="sb-choral-rail-error">
        {state.error}
      </p>
    );
  }
  if (recs.length === 0) {
    return (
      <p className="sb-choral-rail__note" data-testid="sb-choral-rail-empty">
        No governed choral works fit this Sunday yet — the catalog is still growing.
      </p>
    );
  }
  return (
    <>
      <p className="sb-choral-rail__lede" data-testid="sb-choral-rail-lede">
        These candidates are ranked deterministically over the governed
        Choral Intelligence™ catalog — scripture, ritual moment, season,
        parish capability, editorial authority, and this parish’s own
        memory.
      </p>
      <ul className="sb-choral-rail__list" data-testid="sb-choral-rail-list">
        {recs.map((rec) => (
          <RailItem
            key={rec.work.id}
            rec={rec}
            open={openId === rec.work.id}
            onToggle={() => toggle(rec.work.id)}
            sundayDate={sundayDate}
            onPicked={onPicked}
          />
        ))}
      </ul>
    </>
  );
}
